import { LoadingOutlined, MessageOutlined } from '@ant-design/icons';
import { Col, Input, Row, Typography } from "antd";
import { useSession } from 'contexts/SessionContext';
import { useTranslation } from 'contexts/TranslationContext';
import { Formik } from 'formik';
import useAuthHook from 'hooks/useAuthHook';
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { useSignInMutation, useVerifyMutation } from 'services/auth';
import { Button } from "shared/components/Button";
import * as LOCAL_STORAGE_KEYS from 'shared/constants/localStorageKeys';
import routes, { appRoute } from "shared/constants/routes";
import { Header } from "shared/views/Header";
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import gaTrackingEvent from "../utilities/gaTrackingEvent";
import getGeoLocation from "../utilities/getGeoLocation";
import { Label } from 'Components';
import HelpSupport from 'assets/svg/chat.svg'
import Call from 'assets/svg/call.svg'
import Email from 'assets/svg/email.svg'
import BgBottomLeftIcon from 'assets/svg/bgBottomLeft.svg'
import BgTopRightIcon from 'assets/svg/bgTopRight.svg'

const { Text } = Typography;

export const HelpAndContact = () => {
  const { translate } = useTranslation();
  return (
    <div>
      <div className="flex items-center justify-start sm:justify-center flex-row gap-2 align-middle mt-4">
        <img src={HelpSupport} alt="help" className="bg-gray-200 rounded-lg p-2" />
        <Link style={{ fontSize: '14px', color: '#666666', fontWeight: '700' }}>
          {translate('SIGN_IN.HELP_AND_SUPPORT')}
        </Link>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-2 items-start sm:items-center sm:justify-center p-2 sm:bg-[#EEEEEE]">
        <div className="flex flex-row items-center">
          <img src={Call} alt="call" className="rounded-lg p-2" />
          <a
            href="tel:920005477"
            className="text-xs text-black ml-2 font-medium hover:underline"
          >
            {translate("SIGN_IN.HELP_LINE_CALL")}
          </a></div>
        <div className="hidden sm:flex flex-row items-center">
          <p className="text-xs text-black">{translate("SIGN_IN.OR")}</p>
        </div>

        <div className="flex flex-row items-center">
          <img src={Email} alt="email" className="rounded-lg p-2" />
          <p className="text-xs text-black ml-2">{translate("SIGN_IN.HELP_LINE_EMAIL")}</p>
        </div>
      </div>

    </div>
  )
}

const schema = Yup.object().shape({
  iqamaId: Yup.string()
    .length(10, 'ID_10_DIGITS')
    .required('CANNOT_BE_EMPTY'),
  password: Yup.string()
    .required('CANNOT_BE_EMPTY'),
});

const SignInForm = ({
  setStep,
  setId
}) => {
  const [signIn, { isLoading }] = useSignInMutation();
  const { translate } = useTranslation();

  const initialValues = {
    iqamaId: '',
    password: '',
  };

  // Device ID handling
  let deviceId = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_ID, deviceId);
  }

  const onSubmit = async (values, { resetForm }) => {
    //gaTrackingEvent('login', {})
    try {
      const response = await signIn({
        ...values,
        deviceId
      }).unwrap();
      if (response.isOTPSend) {
        setId(values.iqamaId);
        setStep('verification');
        toast.success(translate('SIGN_IN.CREDENTIAL_SUCCESS'));
      } else {
        console.log(response, "response");
        resetForm();
        toast.error(response?.message || translate('SIGN_IN.INVALID_CREDENTIALS'));
      }
    } catch (error) {
      console.log(error, "error");

      if (error?.data?.code) {
        switch (error?.data?.code) {
          case "INVALID_CREDENTIALS":
          case "ACCOUNT_DELETED":
          case "DEVICE_REGISTRATION_LIMIT_REACHED":
          case "IVR_NOT_VERIFIED":
          case "DEVICE_NOT_REGISTERED":
          case "LOGIN_FAILED":
            toast.error(translate(`SIGN_IN.${error?.data?.code}`));
            break;
          case "OTP_SEND_FAILED":
            toast.error(translate(`SIGN_IN.TIMEOUT`))
            break;
          case "ACCOUNT_LOCKED":
            toast.error(error?.data?.message);
            break;
          default:
            toast.error(translate('SIGN_IN.SOMETHING_WENT_WRONG'));
            break;
        }
      }
      else
        toast.error(translate('SIGN_IN.SOMETHING_WENT_WRONG'));
    }
  };

  const gaSignUpEvent = () => {
    gaTrackingEvent('create_account', {});
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}>
      {({ handleSubmit, handleChange, handleBlur, values, errors }) => (
        <form onSubmit={handleSubmit} className="space-y-3 md:space-y-3">
          <div>
            <Label
              label={translate('SIGN_IN.FIELDS.ID')}
              required={true}
              name={translate('SIGN_IN.FIELDS.ID')}
            />
            <Input
              className={'tamweel-base-input'}
              name="iqamaId"
              id="iqamaId"
              placeholder={translate('SIGN_IN.FIELDS.ID')}
              maxLength={10}
              onChange={(e) => {
                // Remove any character that is not an English letter or number
                const value = e.target.value.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669]/g, '');
                handleChange({
                  target: {
                    name: "iqamaId",
                    value: value,
                  },
                });
              }}
              value={values.iqamaId} />
            {
              errors.iqamaId && <Text type="danger">{translate("SIGN_IN." + errors.iqamaId)}</Text>
            }
          </div>
          <div>
            <Label
              label={translate('SIGN_IN.FIELDS.PASSWORD')}
              required={true}
              name={translate('SIGN_IN.FIELDS.PASSWORD')}
            />
            <Input.Password
              className={'tamweel-base-input'}
              status={errors.password ? "error" : null}
              name="password"
              id="password"
              placeholder={translate('SIGN_IN.FIELDS.PASSWORD')}
              onChange={(e) => {
                const value = e.target.value.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669]/g, '');
                handleChange({
                  target: {
                    name: "password",
                    value: value,
                  },
                });
              }}
              value={values.password} />
            {
              errors.password && <Text type="danger">{translate("SIGN_IN." + errors.password)}</Text>
            }
          </div>
          <div className="text-center  text-sm">
            <Link style={{ fontSize: '14px', letterSpacing: 1, fontWeight: '400' }} to={routes.AUTH.FORGOT_PASSWORD}>{translate('SIGN_IN.FORGOT_PASSWORD')}</Link>
          </div>
          <div style={{ fontSize: '14px' }} className="uppercase text-center text-sm text-gray-400">
            <span>{translate('SIGN_IN.DONT_HAVE_AN_ACCOUNT')} </span>
            <Link className="text-primary" to={routes.AUTH.SIGN_UP} onClick={gaSignUpEvent}>{translate('SIGN_IN.SIGN_UP')}</Link>
          </div>
          <Button
            className={'tamweel-button mb-4'}
            type="submit" disabled={isLoading} block>
            {
              isLoading ? <LoadingOutlined /> : translate('SIGN_IN.LOGIN')
            }
          </Button>
        </form>
      )}
    </Formik>
  );
};

const SignInVerification = ({ setStep, id }) => {
  const [seconds, setSeconds] = useState(60);
  const [otp, setOtp] = useState(['', '', '', '']);

  const { signIn } = useSession();

  // Device ID handling
  let deviceId = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_ID, deviceId);
  }

  const otpRef1 = useRef(null);
  const otpRef2 = useRef(null);
  const otpRef3 = useRef(null);
  const otpRef4 = useRef(null);

  const [verify, { isLoading }] = useVerifyMutation();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { language } = useTranslation();

  const countdown = (intervalId) => {
    if (seconds <= 0) {
      setStep('form');
      toast.error(translate('SIGN_IN.TIMEOUT'));
      clearInterval(intervalId);
    } else {
      setSeconds(seconds - 1);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      countdown(intervalId);
    }, 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [seconds]); // eslint-disable-line

  const handleChange = (value, index) => {
    setOtp((otp) => otp.map((_, i) => (index === i ? value : _)));

    switch (index) {
      case 0:
        otpRef2.current.focus();
        break;
      case 1:
        otpRef3.current.focus();
        break;
      case 2:
        otpRef4.current.focus();
        break;
      default:
        return;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const { latitude, longitude } = await getGeoLocation()

    try {
      const response = await verify({
        otp: otp.join(''),
        iqamaId: id,
        deviceId,
        ObjLatLng: {
          lat: latitude ?? '',
          lang: longitude ?? '',
        },
      }).unwrap();

      if (response?.verified) {
        signIn(response?.user);
        navigate(appRoute(routes.PRE_APP));
        let userObj = response.user;
        userObj = {
          ...userObj,
          token: response.token,
        };

        localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(userObj));
        toast.success(translate('SIGN_IN.VERIFICATION_COMPLETE'));
      } else {
        toast.error(translate('SIGN_IN.WRONG_OTP'));
        setStep('form');
      }
    } catch (error) {
      toast.error(translate('SIGN_IN.SOMETHING_WENT_WRONG'));
    }
  };

  return (
    <>
      <div className="text-gray-400">{translate('SIGN_IN.WE_HAVE_SENT')}</div>

      <form onSubmit={onSubmit} className="space-y-6 mt-6">
        <div className="flex justify-between w-full max-w-xl mx-auto" dir='ltr'>
          {[otpRef1, otpRef2, otpRef3, otpRef4].map((ref, index) => (
            <input
              dir='ltr'
              key={index}
              ref={ref}
              maxLength={1}
              type='text'
              pattern="[0-9]*"
              inputMode="numeric"
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d$/.test(value)) {
                  handleChange(value, index);
                } else {
                  e.target.value = '';
                }
              }}
              onKeyDown={(e) => {
                if (
                  !/^\d$/.test(e.key) &&
                  e.key !== 'Backspace' &&
                  e.key !== 'Tab' &&
                  e.key !== 'ArrowLeft' &&
                  e.key !== 'ArrowRight'
                ) {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => e.target.blur()} // disables mouse wheel input
              onPaste={(e) => e.preventDefault()} // disables pasting
              name={`otp${index + 1}`}
              id={`otp${index + 1}`}
              className="text-2xl font-semibold border border-gray-300 rounded-lg w-[22%] h-16 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 text-center no-spinner"
              required
            />
          ))}
        </div>


        <div className="text-center text-gray-400 font-bold text-3xl">
          00:{String(seconds).padStart(2, '0')}
        </div>

        <div className="text-center text-gray-400 font-bold text-sm cursor-not-allowed">
          {translate('SIGN_IN.RESEND_CODE')}
        </div>

        <Button type="submit" disabled={isLoading} block className="bg-yellow-400 hover:bg-yellow-500 text-white font-semibold text-base py-2 rounded-lg shadow">
          {isLoading ? <LoadingOutlined /> : translate('SIGN_IN.SEND')}
        </Button>
      </form>
    </>

  );
};

export default function SignIn() {
  const [step, setStep] = useState('form');
  const [id, setId] = useState(null);
  const { logout } = useAuthHook();
  const { translate } = useTranslation();

  useEffect(() => {
    logout();
  }, []);

  useEffect(() => {
    // Create script element
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = `
      (function(w,d,s,r,k,h,m){
          if(w.performance && w.performance.timing && w.performance.navigation) {
              w[r] = w[r] || function(){(w[r].q = w[r].q || []).push(arguments)};
              h=d.createElement('script');h.async=true;h.setAttribute('src',s+k);
              d.getElementsByTagName('head')[0].appendChild(h);
              (m = window.onerror),(window.onerror = function (b, c, d, f, g) {
                  m && m(b, c, d, f, g),g || (g = new Error(b)),(w[r].q = w[r].q || []).push(["captureException",g]);
              });
          }
      })(window,document,'https://192.168.130.82:7443/RUMScript.js?appKey=','apmr','Mu6DF7oJGkf5qb9uqkbXwPZ0RxAq33P410008056');
    `;

    // Append script to head
    document.head.appendChild(script);

    // Cleanup on component unmount
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section className="relative h-screen bg-[#F3F4F6] overflow-hidden">
      {/* Top-right image */}
      <img
        src={BgTopRightIcon}
        alt="Top Right Decoration"
        className="absolute top-0 right-0 w-64 md:w-64 lg:w-64 z-0"
      />

      {/* Bottom-left image */}
      <img
        src={BgBottomLeftIcon}
        alt="Bottom Left Decoration"
        className="absolute bottom-0 left-0 w-64 md:w-64 lg:w-64 z-0"
      />

      <Header />
      <div className="relative z-10 flex flex-col h-full pb-10">

        {/* Scrollable only if content exceeds screen height */}
        <div className="flex-1 overflow-auto  [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-8 md:py-12 min-h-full w-full">
            <div className="w-full max-w-md md:max-w-lg lg:max-w-2xl">
              <div className="bg-white shadow-md rounded-3xl px-8 py-8 md:px-10 md:py-10 lg:px-12 lg:py-12">
                <h1 className="form-header-text tamweel-text-22 font-bold leading-tight tracking-tight text-primary uppercase">
                  {translate('SIGN_IN.TITLE')}
                </h1>

                <div className="bg-secondary w-9 h-1" />

                <div className="form-header-title-wrapper">
                  <h2 className="uppercase text-black text-2xl sm:text-3xl font-bold my-6">
                    {translate('SIGN_IN.DESCRIPTION')}
                  </h2>
                </div>

                {step === 'form' && <SignInForm setId={setId} setStep={setStep} />}
                {step === 'verification' && <SignInVerification id={id} setStep={setStep} />}

                <HelpAndContact />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>



  );
}
